import type {Location} from "@khan/urls";

import {validateContinueUrl} from "./validate-continue-url.ts";

/**
 * Get the (validated) continue URL from the from the passed in location.
 *
 * This is the value of the `continue` query parameter. We validate the URL is
 * a safe redirect target by checking that it's a Khan Academy URL. If we
 * didn't do this validation, someone would be able to link to a valid Khan
 * Academy page that could then redirect the user to a malicious site. See:
 * https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html
 *
 * @param location The location object.
 * @returns The continue URL (path) OR null if not found.
 */
export const getContinueUrl = (location: Location): null | string => {
    // Note: the `|| ""`` is needed below because the `continue` lookup can be
    // undefined (even though the typescript types don't reflect this). To
    // catch this case we'd need to enable the typescript
    // noUncheckedIndexedAccess setting.
    const unsafeContinueUrl = location.queryParams.continue || "";

    if (!unsafeContinueUrl) {
        return null;
    }

    const validatedContinueUrl = validateContinueUrl(unsafeContinueUrl);

    if (!validatedContinueUrl) {
        return null;
    }

    return validatedContinueUrl;
};
